// Real data service wrapper - Main entry point for all organizational data via HTTP API  
// This file acts as a central hub that imports and re-exports from specialized HTTP API services
// Following the KISS principle: each service has a single responsibility and uses HTTP API calls

// ============================================================================
// 🚀 TYPE DEFINITIONS
// ============================================================================

// Import types for internal use and re-export
import type { MonumentaEmployee } from './services/employee.service';
import type { OrganizationalHierarchyNode } from './services/hierarchy.service';
import type { FinancialMetrics, ClientResult } from './services/financial.service';
import type { DirectorTarget } from './services/performance.service';
import type { VacationEvent, LegacyVacationBalance } from './services/vacation.service';
import type { VacationBalance } from '@/types/vacation';
import type { DepartmentAnalytics, EmployeeTimeline, PerformanceInsights } from './services/hr-analytics.service';
import { createImpersonatedApiHeaders, logApiCall } from './services/api-utils';

// Re-export core types
export type {
  MonumentaEmployee,
  OrganizationalHierarchyNode,
  FinancialMetrics,
  ClientResult,
  DirectorTarget,
  VacationEvent,
  VacationBalance,
  DepartmentAnalytics,
  EmployeeTimeline,
  PerformanceInsights
};

// Additional types for service functions
export interface UserSubordinatesOptions {
  userEmail?: string;
  department?: string;
  performanceCategory?: string;
}

export interface TeamSummary {
  totalTeamSize: number;
  averageVacationBalance: number;
  performanceDistribution: Record<string, number>;
  departmentDistribution: Record<string, number>;
  performanceMetrics: {
    highPerformers: number;
    potentialGrowth: number;
    needsDevelopment: number;
  };
}

export interface SystemIntegrityResult {
  success: boolean;
  message?: string;
}

export interface ValidationScenarioResult {
  success: boolean;
  errorMessage?: string;
}

export interface SystemStatusReport {
  totalEmployees: number;
  leadershipTeam: number;
  currentUser: MonumentaEmployee | null;
  userRole?: string;
  department?: string;
  subordinates: number;
  isManager: boolean;
  systemReady: boolean;
}

export interface EmailValidationInfo {
  exactMatch: boolean;
  caseInsensitiveMatch: boolean;
  resilientPatternMatch: boolean;
  patternMatchUser: { name: string; email: string } | null;
  searchedEmail: string;
  extractedPattern: { firstLetter: string; namePart: string; domain: string } | null;
  similarEmails: string[];
  totalEmployees: number;
  allEmails: string[];
}

export interface FullNameValidationInfo {
  searchedName: string;
  exactFullNameMatch: boolean;
  caseInsensitiveFullNameMatch: boolean;
  caseInsensitiveNameMatch: boolean;
  similarNames: string[];
  totalEmployees: number;
  allNames: string[];
}

// Function return types for dynamic functions
export type GetCurrentUserSubordinatesReturn = MonumentaEmployee[];
export type GetCurrentUserSubordinatesByDepartmentReturn = MonumentaEmployee[];
export type GetCurrentUserSubordinatesByPerformanceReturn = MonumentaEmployee[];
export type GetCurrentUserTeamSummaryReturn = TeamSummary;

// Validation and testing function types
export type ValidateSystemIntegrityReturn = boolean;
export type TestInvalidFullNameScenarioReturn = boolean;
export type TestEdgeCaseScenariosReturn = void;
export type PrintSystemStatusReturn = void;

// Service configuration types
export interface ServiceConfiguration {
  enableLogging?: boolean;
  enableValidation?: boolean;
  enableSystemTesting?: boolean;
}

// Utility types for common operations
export type EmployeeFilterFn = (employee: MonumentaEmployee) => boolean;
export type DepartmentName = string;
export type PerformanceCategory = string;
export type UserEmail = string;
export type FullName = string;

// Omit utility types for common data transformations
export type EmployeeBasicInfo = Omit<MonumentaEmployee, 'salary' | 'totalCost' | 'vacationBalance'>;
export type EmployeePublicData = Omit<MonumentaEmployee, 'salary' | 'totalCost'>;
export type EmployeeContactInfo = Pick<MonumentaEmployee, 'id' | 'name' | 'email' | 'department' | 'role'>;
export type EmployeePerformanceData = Pick<MonumentaEmployee, 'id' | 'name' | 'email' | 'performance' | 'department' | 'role'>;

// Team management types
export type TeamMemberRole = 'leader' | 'coordinator' | 'member';
export interface TeamMember extends EmployeeContactInfo {
  role: TeamMemberRole;
  subordinatesCount?: number;
}

// Dashboard configuration types
export interface DashboardUser extends EmployeeContactInfo {
  isManager: boolean;
  teamSize: number;
}

// Search and filter result types
export type SearchResult<T> = {
  results: T[];
  totalCount: number;
  hasMore: boolean;
};

export type EmployeeSearchResult = SearchResult<MonumentaEmployee>;
export type TeamSearchResult = SearchResult<TeamMember>;

// ============================================================================
// 🚀 SERVICE IMPORTS
// ============================================================================

// String utilities and formatting
export * from './services/string-utils.service';

// Employee management and user dashboard
export * from './services/employee.service';

// Financial metrics and client data
export * from './services/financial.service';

// Re-export specific functions that are commonly used
export { getClientResultsSIG } from './services/financial.service';

// Performance tracking and goals (includes director targets 2025)
export * from './services/performance.service';

// Vacation calendar and balance management
export * from './services/vacation.service';
export { getVacationEventsByMonth } from './services/vacation.service';

// Supplier management and analytics
export * from './services/supplier.service';

// Organizational hierarchy navigation
export * from './services/hierarchy.service';

// HR analytics and department insights
export * from './services/hr-analytics.service';

// Recruitment candidate management
export * from './services/recruitment.service';

// Training and courses (comprehensive interface)
export interface Course {
  id: string;
  title: string;
  provider: string;
  description: string;
  category: 'technical' | 'behavioral' | 'compliance' | 'leadership' | 'external';
  type: 'mandatory' | 'recommended' | 'optional' | 'external';
  status: 'available' | 'draft' | 'completed' | 'in-progress' | 'not-started' | 'cancelled';
  duration: number;
  completionCertificate?: boolean;
  subsidized?: boolean;
  subsidyTerms?: string;
  // Additional properties for course management
  priority?: 'critical' | 'high' | 'medium' | 'low';
  progress?: number;
  requiredBy?: string;
  skills?: string[];
  rating?: number;
  cost?: number;
  currency?: 'USD' | 'BRL';
  startDate?: string;
  completionDate?: string;
  certificateUrl?: string;
  feedback?: string;
}

// Extended interface for team course management
export interface TeamCourse extends Course {
  enrolledEmployees: Array<{
    id: string;
    name: string;
    status: 'completed' | 'in-progress' | 'not-started';
    progress?: number;
    enrollmentDate?: string;
    completionDate?: string;
  }>;
  teamCompletionRate: number;
}

export const getAvailableCourses = (): Course[] => {
  // TODO: Replace with API call to fetch courses
  // For now, return empty array to show proper empty states
  return [];
};

export const getCoursesByStatus = (courses: Course[], status: string): Course[] => {
  return courses.filter(course => course.status === status);
};

export const getCoursesByCategory = (courses: Course[], category: string): Course[] => {
  return courses.filter(course => course.category === category);
};

export const getCoursesByType = (courses: Course[], type: string): Course[] => {
  return courses.filter(course => course.type === type);
};

// Alias for consistency with other data service functions
export const getAllCourses = getAvailableCourses;

// ============================================================================
// 🚀 SPECIALIZED SERVICE AGGREGATION
// ============================================================================
// These convenience functions use multiple services to provide integrated functionality

import { get } from 'aws-amplify/api'
import { getAllMonumentaEmployees, getLeadershipKeywords } from './services/employee.service';
import { caseInsensitiveEquals } from './services/string-utils.service';
import { getLeadershipFromHierarchy } from './services/hierarchy.service';
import { validateUserEmailExists, syncWithAuthUser } from './services/employee.service';

// Dynamic function to get user's subordinates (integrates employee + hierarchy services) - HTTP API version
export const getCurrentUserSubordinates = async (userEmail: string): Promise<GetCurrentUserSubordinatesReturn> => {
  // try {
  //   const user = (await getAllMonumentaEmployees()).find(emp => caseInsensitiveEquals(emp.email, userEmail));

  //   if (!user) {
  //     throw new Error(
  //       `🚨 CRITICAL ERROR: Cannot find user with email '${userEmail}'.\n` +
  //       `User not found in organizational data.`
  //     );
  //   }
    
  //   logApiCall(`/monumenta/employees/by-email/${user.email}/subordinates`);
    
  //   // Use the subordinates API endpoint - following documents.ts pattern
  //   const { response } = await get({
  //     apiName: 'monumenta',
  //     path: `/monumenta/employees/by-email/${encodeURIComponent(user.email)}/subordinates`,
  //     options: {
  //       headers: createImpersonatedApiHeaders()
  //     }
  //   })
    
  //   const result = await response
  //   const data = await result.body.json() as { subordinates?: MonumentaEmployee[] }

  //   return data?.subordinates || [];
  // } catch (error) {
  //   console.error('Failed to get subordinates:', error);
    return [];
  // }
};

// Dynamic function to get subordinates by department - HTTP API version
export const getCurrentUserSubordinatesByDepartment = async (department: string, userEmail: string): Promise<GetCurrentUserSubordinatesByDepartmentReturn> => {
  const subordinates = await getCurrentUserSubordinates(userEmail);
  if (department === 'ALL') return subordinates;
  return subordinates.filter(emp => caseInsensitiveEquals(emp.department, department));
};

// Dynamic function to get subordinates by performance category - HTTP API version
export const getCurrentUserSubordinatesByPerformance = async (performanceCategory: string, userEmail: string): Promise<GetCurrentUserSubordinatesByPerformanceReturn> => {
  const subordinates = await getCurrentUserSubordinates(userEmail);
  return subordinates.filter(emp => caseInsensitiveEquals(emp.performance, performanceCategory));
};

// Dynamic function to get team performance summary - HTTP API version
export const getCurrentUserTeamSummary = async (userEmail: string): Promise<GetCurrentUserTeamSummaryReturn> => {
  // try {
    
  //   logApiCall(`/monumenta/employees/by-email/${userEmail}/team-summary`);
    
  //   // Use team summary API endpoint - following documents.ts pattern
  //   const { response } = await get({
  //     apiName: 'api',
  //     path: `/monumenta/employees/by-email/${encodeURIComponent(userEmail)}/team-summary`,
  //     options: {
  //       headers: createImpersonatedApiHeaders()
  //     }
  //   })
    
  //   const result = await response
  //   const data = await result.body.json() as { teamSummary?: TeamSummary }

  //   return data?.teamSummary || {
  //     totalTeamSize: 0,
  //     averageVacationBalance: 0,
  //     performanceDistribution: {},
  //     departmentDistribution: {},
  //     performanceMetrics: {
  //       highPerformers: 0,
  //       potentialGrowth: 0,
  //       needsDevelopment: 0
  //     }
  //   };
  // } catch (error) {
  //   console.error('Failed to get team summary:', error);
    return {
      totalTeamSize: 0,
      averageVacationBalance: 0,
      performanceDistribution: {},
      departmentDistribution: {},
      performanceMetrics: {
        highPerformers: 0,
        potentialGrowth: 0,
        needsDevelopment: 0
      }
    };
  // }
};

// ============================================================================
// 🧪 SYSTEM TESTING AND VALIDATION
// ============================================================================

// Test function to validate system integrity - HTTP API version
export const validateSystemIntegrity = async (): Promise<ValidateSystemIntegrityReturn> => {
  try {
    // Test 1: Check if leadership team exists and has valid data via API
    const leadership = await getLeadershipFromHierarchy();
    if (leadership.length === 0) {
      throw new Error('No leadership team found via API');
    }

    // Test 1.1: Validate leadership team data integrity
    leadership.forEach((leader, index) => {
      if (!leader.email || !leader.name || !leader.role) {
        throw new Error(`Leadership member ${index} has invalid data: ${JSON.stringify(leader)}`);
      }
    });

    // Test 1.2: Check if there's at least one DIRETOR
    const directorExists = leadership.some(user => user.role.toUpperCase().includes('DIRETOR'));
    if (!directorExists) {
      console.warn('⚠️  No DIRETOR found - may cause issues if director tries to log in');
    }

    // Test 5: Test email validation function
    const allEmployees = await getAllMonumentaEmployees();
    if (allEmployees.length > 0) {
      const sampleEmail = allEmployees[0].email;
      const emailExists = await validateUserEmailExists(sampleEmail);
      if (!emailExists) {
        throw new Error(`Email validation failed for sample user: ${sampleEmail}`);
      }
    }

    return true;
  } catch (error) {
    console.error('🚨 System integrity test failed:', error);
    return false;
  }
};

// Test function to simulate invalid full name scenario - HTTP API version
export const testInvalidFullNameScenario = async (invalidFullName: string): Promise<TestInvalidFullNameScenarioReturn> => {
  try {
    await syncWithAuthUser(invalidFullName);
    console.error('🚨 ERROR: Invalid full name should have thrown exception!');
    return false;
  } catch (error) {
    return true;
  }
};

// Test function to simulate edge case scenarios
export const testEdgeCaseScenarios = async (): Promise<TestEdgeCaseScenariosReturn> => {
  // Test 1: Check director availability (for when they log in)
  const leadership = await getLeadershipFromHierarchy();
  const hasDirector = leadership.some(user => user.role.toUpperCase().includes('DIRETOR'));
  if (!hasDirector) {
    console.warn('⚠️  No DIRETOR found - may cause issues if director tries to log in');
    console.warn('Available roles:', leadership.map(u => u.role));
  }

  // Test 2: Validate all leadership emails exist in organizational data
  leadership.forEach(leader => {
    const emailExists = validateUserEmailExists(leader.email);
    if (!emailExists) {
      console.error(`🚨 Leadership member email not found in org data: ${leader.email}`);
    }
  });

  // Test 3: Check for potential data inconsistencies
  const orgEmails = (await getAllMonumentaEmployees()).map(emp => emp.email);
  const duplicateEmails = orgEmails.filter((email, index) => orgEmails.indexOf(email) !== index);
  if (duplicateEmails.length > 0) {
    console.warn('⚠️  Duplicate emails found in organizational data:', duplicateEmails);
  }
  
  // Test 4: Check leadership data quality
  leadership.forEach((leader, index) => {
    if (!leader.email || !leader.name || !leader.role) {
      console.error(`🚨 Leadership member ${index} has incomplete data:`, leader);
    }
  });
};

// System status report function - HTTP API version
export const printSystemStatus = async (): Promise<PrintSystemStatusReturn> => {
  try {
    const [leadership, allEmployees] = await Promise.all([
      getLeadershipFromHierarchy(),
      getAllMonumentaEmployees()
    ]);
    // System status available for inspection via debugging tools
  } catch (error) {
    console.error('🚨 Error generating system status:', error);
  }
};

// ============================================================================
// 📚 ONBOARDING SYSTEM
// ============================================================================

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  category: 'documentation' | 'training' | 'meeting' | 'system';
  duration?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  assignedTo?: string;
  dueDate?: string;
  completedDate?: string;
  priority: 'high' | 'medium' | 'low';
  dependencies?: string[];
  resources?: string[];
}

export interface OnboardingEmployee {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  startDate: string;
  manager?: string;
  onboardingStatus: 'not_started' | 'in_progress' | 'completed';
  completedSteps: number;
  totalSteps: number;
  currentStep?: string;
}

// Generate onboarding steps based on role
export const getOnboardingSteps = (role?: string): OnboardingStep[] => {
  const baseSteps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Boas-vindas e Apresentação da Empresa',
      description: 'Conhecer a história, missão e valores da Monumenta',
      category: 'documentation',
      duration: '2 horas',
      status: 'pending',
      priority: 'high',
      resources: ['Manual do Colaborador', 'Vídeo Institucional']
    },
    {
      id: 'systems',
      title: 'Configuração de Sistemas e Acessos',
      description: 'Configurar email, Wrike, sistemas internos e acessos necessários',
      category: 'system',
      duration: '3 horas',
      status: 'pending',
      priority: 'high',
      dependencies: ['welcome']
    },
    {
      id: 'team_meeting',
      title: 'Reunião com a Equipe',
      description: 'Conhecer os membros da equipe e projetos em andamento',
      category: 'meeting',
      duration: '1 hora',
      status: 'pending',
      priority: 'medium',
      dependencies: ['systems']
    },
    {
      id: 'compliance',
      title: 'Treinamentos Obrigatórios',
      description: 'LGPD, Diversidade e Inclusão, e outros cursos obrigatórios',
      category: 'training',
      duration: '6 horas',
      status: 'pending',
      priority: 'high',
      dependencies: ['systems']
    }
  ];

  // Add role-specific steps
  if (role?.toUpperCase().includes('DESENVOLVEDOR') || role?.toUpperCase().includes('ENGENHEIRO')) {
    baseSteps.push({
      id: 'tech_setup',
      title: 'Setup do Ambiente de Desenvolvimento',
      description: 'Configurar ambiente local, repos, e ferramentas de desenvolvimento',
      category: 'system',
      duration: '4 horas',
      status: 'pending',
      priority: 'high',
      dependencies: ['systems']
    });
  }

  if (role?.toUpperCase().includes('GERENTE') || role?.toUpperCase().includes('COORDENADOR')) {
    baseSteps.push({
      id: 'management_intro',
      title: 'Introdução à Gestão na Monumenta',
      description: 'Processos de gestão, ferramentas de liderança e responsabilidades',
      category: 'training',
      duration: '3 horas',
      status: 'pending',
      priority: 'high',
      dependencies: ['team_meeting']
    });
  }

  return baseSteps;
};

// Get onboarding employees (new hires in their first 90 days)
export const getOnboardingEmployees = async (managerEmail?: string): Promise<OnboardingEmployee[]> => {
  const allEmployees = await getAllMonumentaEmployees();
  const currentDate = new Date();

  // Get subordinates once if manager is specified
  const subordinates = managerEmail ? await getCurrentUserSubordinates(managerEmail) : null;

  return allEmployees
    .filter(emp => {
      // Filter new employees (assuming those without extensive data are recent hires)
      const isNewHire = !emp.performance || emp.performance === 'SOB ANÁLISE' || emp.vacationBalance === 0;
      if (!isNewHire) return false;

      // If manager specified, only show their direct reports
      if (managerEmail && subordinates) {
        return subordinates.some(sub => sub.email === emp.email);
      }

      return true;
    })
    .map(emp => {
      const steps = getOnboardingSteps(emp.role);
      const completedSteps = Math.floor(Math.random() * (steps.length + 1)); // Random progress for demo
      
      return {
        id: emp.id.toString(), // Convert to string to match interface
        name: emp.name,
        email: emp.email,
        role: emp.role,
        department: emp.department,
        startDate: new Date(currentDate.getTime() - (Math.random() * 60 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0], // Random start date in last 60 days
        manager: 'TBD', // Will be set from hierarchy data in real implementation
        onboardingStatus: completedSteps === 0 ? 'not_started' : 
                         completedSteps === steps.length ? 'completed' : 'in_progress',
        completedSteps,
        totalSteps: steps.length,
        currentStep: completedSteps < steps.length ? steps[completedSteps].id : undefined
      };
    });
};

// Get specific onboarding employee by ID
export const getOnboardingEmployeeById = async (id: string): Promise<OnboardingEmployee | null> => {
  const onboardingEmployees = await getOnboardingEmployees();
  return onboardingEmployees.find(emp => emp.id === id) || null;
};

// Update onboarding step status
export const updateOnboardingStepStatus = (employeeId: string, stepId: string, status: OnboardingStep['status']): boolean => {
  // In a real implementation, this would update a database
  // For now, we'll just return true to simulate success
  return true;
};

// Get onboarding progress summary
export const getOnboardingProgress = async (managerEmail?: string): Promise<{
  total: number;
  notStarted: number;
  inProgress: number;
  completed: number;
  overdue: number;
}> => {
  const employees = await getOnboardingEmployees(managerEmail);

  return {
    total: employees.length,
    notStarted: employees.filter(emp => emp.onboardingStatus === 'not_started').length,
    inProgress: employees.filter(emp => emp.onboardingStatus === 'in_progress').length,
    completed: employees.filter(emp => emp.onboardingStatus === 'completed').length,
    overdue: employees.filter(emp => {
      // Consider overdue if in progress for more than 30 days
      const startDate = new Date(emp.startDate);
      const thirtyDaysLater = new Date(startDate.getTime() + (30 * 24 * 60 * 60 * 1000));
      return emp.onboardingStatus === 'in_progress' && new Date() > thirtyDaysLater;
    }).length
  };
};
